<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  error: { type: String, default: '' },
  offer: { type: Object, default: null },
  paymentOptions: { type: Array, default: () => [] },
  saving: { type: Boolean, default: false },
  show: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'submit']);
const { t } = useI18n();
const localError = ref('');
const form = ref({});

const valueFrom = (source, snakeKey, camelKey = '') =>
  source?.[snakeKey] ?? source?.[camelKey] ?? '';

const booleanFrom = value => value === true || value === 'true' || value === 1;

const initialForm = offer => ({
  offer_amount: valueFrom(offer, 'offer_amount', 'offerAmount'),
  financed_amount: valueFrom(offer, 'financed_amount', 'financedAmount'),
  payment_kind: valueFrom(offer, 'payment_kind', 'paymentKind'),
  first_purchase: booleanFrom(
    valueFrom(offer, 'first_purchase', 'firstPurchase')
  ),
  has_sfh: booleanFrom(valueFrom(offer, 'has_sfh', 'hasSfh')),
  booking_reference: valueFrom(offer, 'booking_reference', 'bookingReference'),
  notes: valueFrom(offer, 'notes'),
});

const isPurchase = computed(
  () =>
    valueFrom(props.offer, 'business_context', 'businessContext') === 'purchase'
);

const close = () => {
  if (!props.saving) emit('close');
};

const submit = () => {
  localError.value = '';
  if (form.value.offer_amount === '' || form.value.offer_amount == null) {
    localError.value = t('PROPERTY_OFFERS.EDIT.VALIDATION.AMOUNT_REQUIRED');
    return;
  }

  emit('submit', { ...form.value });
};

watch(
  () => props.show,
  open => {
    if (!open) return;

    form.value = initialForm(props.offer);
    localError.value = '';
  },
  { immediate: true }
);
</script>

<template>
  <LaisModal
    :show="show"
    :title="t('PROPERTY_OFFERS.EDIT.TITLE')"
    max-width-class="max-w-[860px]"
    @close="close"
  >
    <form class="grid gap-5" @submit.prevent="submit">
      <section
        class="rounded-lais-lg border border-lais-border-soft bg-white p-4 sm:p-5"
      >
        <div class="grid gap-1">
          <h3 class="font-redHatDisplay text-lg font-semibold text-lais-text">
            {{ t('PROPERTY_OFFERS.EDIT.VALUES_TITLE') }}
          </h3>
          <p class="text-sm text-lais-text-muted">
            {{ t('PROPERTY_OFFERS.EDIT.DESCRIPTION') }}
          </p>
        </div>

        <div class="mt-5 grid gap-4 sm:grid-cols-2">
          <label class="grid gap-1.5 text-sm font-semibold text-lais-text">
            {{ t('PROPERTY_OFFERS.EDIT.FIELDS.AMOUNT') }}
            <input
              v-model="form.offer_amount"
              type="number"
              min="0"
              step="0.01"
              class="h-11 rounded-lais border border-lais-border bg-white px-3 text-sm font-medium text-lais-text outline-none focus:border-lais-primary focus:ring-2 focus:ring-lais-primary/10"
            />
          </label>

          <label
            v-if="isPurchase"
            class="grid gap-1.5 text-sm font-semibold text-lais-text"
          >
            {{ t('PROPERTY_OFFERS.EDIT.FIELDS.FINANCED_AMOUNT') }}
            <input
              v-model="form.financed_amount"
              type="number"
              min="0"
              step="0.01"
              class="h-11 rounded-lais border border-lais-border bg-white px-3 text-sm font-medium text-lais-text outline-none focus:border-lais-primary focus:ring-2 focus:ring-lais-primary/10"
            />
          </label>

          <label
            v-if="isPurchase"
            class="grid gap-1.5 text-sm font-semibold text-lais-text"
          >
            {{ t('PROPERTY_OFFERS.EDIT.FIELDS.PAYMENT_KIND') }}
            <select
              v-model="form.payment_kind"
              class="h-11 rounded-lais border border-lais-border bg-white px-3 text-sm font-medium text-lais-text outline-none focus:border-lais-primary focus:ring-2 focus:ring-lais-primary/10"
            >
              <option value="">{{ t('PROPERTY_OFFERS.EDIT.SELECT') }}</option>
              <option
                v-for="option in paymentOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>

          <label class="grid gap-1.5 text-sm font-semibold text-lais-text">
            {{ t('PROPERTY_OFFERS.EDIT.FIELDS.BOOKING_REFERENCE') }}
            <input
              v-model="form.booking_reference"
              type="text"
              class="h-11 rounded-lais border border-lais-border bg-white px-3 text-sm font-medium text-lais-text outline-none focus:border-lais-primary focus:ring-2 focus:ring-lais-primary/10"
            />
          </label>
        </div>

        <div v-if="isPurchase" class="mt-4 flex flex-wrap gap-3">
          <label
            class="inline-flex items-center gap-2 rounded-lais border border-lais-border-soft bg-lais-bg-soft px-3 py-2 text-sm font-medium text-lais-text"
          >
            <input v-model="form.first_purchase" type="checkbox" />
            {{ t('PROPERTY_OFFERS.EDIT.FIELDS.FIRST_PURCHASE') }}
          </label>
          <label
            class="inline-flex items-center gap-2 rounded-lais border border-lais-border-soft bg-lais-bg-soft px-3 py-2 text-sm font-medium text-lais-text"
          >
            <input v-model="form.has_sfh" type="checkbox" />
            {{ t('PROPERTY_OFFERS.EDIT.FIELDS.HAS_SFH') }}
          </label>
        </div>
      </section>

      <section
        class="rounded-lais-lg border border-lais-border-soft bg-white p-4 sm:p-5"
      >
        <label class="grid gap-1.5 text-sm font-semibold text-lais-text">
          {{ t('PROPERTY_OFFERS.EDIT.FIELDS.NOTES') }}
          <textarea
            v-model="form.notes"
            rows="5"
            class="rounded-lais border border-lais-border bg-white px-3 py-2 text-sm text-lais-text outline-none focus:border-lais-primary focus:ring-2 focus:ring-lais-primary/10"
          />
        </label>
      </section>

      <p
        v-if="localError || error"
        role="alert"
        class="rounded-lais border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
      >
        {{ localError || error }}
      </p>
    </form>

    <template #footer>
      <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <LaisPrimaryButton variant="ghost" :disabled="saving" @click="close">
          {{ t('PROPERTY_OFFERS.EDIT.CANCEL') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          icon="i-lucide-save"
          :is-loading="saving"
          @click="submit"
        >
          {{ t('PROPERTY_OFFERS.EDIT.SAVE') }}
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>
</template>
